import React, { useState, useMemo, useLayoutEffect } from 'react';
import { Admin, Resource } from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { AnimalList, AnimalEdit } from './animals';
import { RouteList, RouteEdit } from './routes';
import { PopularAnimalsList, PopularAnimalsEdit } from './popularAnimals';
import { NewsList, NewsEdit } from './news';
import { EventsList, EventsEdit } from './events';
import { AdminUserList, AdminUserCreate, AdminUserEdit } from './adminUsers';
import { GuestAppealsList } from './guestAppeals';
import { ExternalReviewsList } from './reviews';
import { QuestList, QuestCreate, QuestEdit } from './quests';
import { RewardList, RewardCreate, RewardEdit } from './rewards';
import { PurchaseList } from './purchases';
import authProvider from './authProvider';
import { CustomLayout } from './layout/CustomLayout';
import { dataProvider } from './dataProvider';
import { lightTheme, darkTheme } from './theme';
import { customRussianMessages } from './i18n/ru';
import { LoginPage } from './LoginPage';
import { HomeDashboard } from './dashboard';

const i18nProvider = polyglotI18nProvider(() => customRussianMessages, 'ru');

const App = () => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const saved = localStorage.getItem('theme');
        return saved === 'dark';
    });

    const theme = useMemo(() => {
        return isDarkMode ? darkTheme : lightTheme;
    }, [isDarkMode]);

    const handleToggleTheme = () => {
        setIsDarkMode((prev) => {
            const newMode = !prev;
            localStorage.setItem('theme', newMode ? 'dark' : 'light');
            return newMode;
        });
    };

    useLayoutEffect(() => {
        document.body.classList.toggle('theme-dark', isDarkMode);
        document.body.classList.toggle('theme-light', !isDarkMode);
    }, [isDarkMode]);

    return (
        <Admin 
            dataProvider={dataProvider} 
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            loginPage={LoginPage}
            dashboard={HomeDashboard}
            layout={(props) => (
                <CustomLayout 
                    {...props} 
                    onToggleTheme={handleToggleTheme} 
                    isDarkMode={isDarkMode} 
                />
            )}
            theme={theme}
        >
            <Resource 
                name="animals" 
                list={AnimalList} 
                edit={AnimalEdit}
                options={{ label: 'Животные' }} 
            />
            <Resource 
                name="routes" 
                list={RouteList} 
                edit={RouteEdit}
                options={{ label: 'Тематические маршруты' }} 
            />
            <Resource 
                name="popular-animals" 
                list={PopularAnimalsList} 
                edit={PopularAnimalsEdit}
                options={{ label: 'Популярные животные' }} 
            />
            <Resource 
                name="news" 
                list={NewsList} 
                edit={NewsEdit}
                options={{ label: 'Новости' }} 
            />
            <Resource 
                name="events" 
                list={EventsList} 
                edit={EventsEdit}
                options={{ label: 'Мероприятия' }} 
            />
            <Resource
                name="admin-users"
                list={AdminUserList}
                create={AdminUserCreate}
                edit={AdminUserEdit}
                options={{ label: 'Учетные записи' }}
            />
            <Resource
                name="guest-users"
                list={GuestAppealsList}
                options={{ label: 'Гости и обращения' }}
            />
            <Resource
                name="external-reviews"
                list={ExternalReviewsList}
                options={{ label: 'Отзывы' }}
            />
            <Resource
                name="quests"
                list={QuestList}
                create={QuestCreate}
                edit={QuestEdit}
                options={{ label: 'Квесты' }}
            />
            <Resource
                name="rewards"
                list={RewardList}
                create={RewardCreate}
                edit={RewardEdit}
                options={{ label: 'Квесты' }}
            />
            <Resource
                name="quest-purchases"
                list={PurchaseList}
                options={{ label: 'Квесты' }}
            />
        </Admin>
    );
};

export default App;
